/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import { useEffect, useRef, useState, useCallback, type ChangeEvent, SyntheticEvent, HTMLAttributes } from "react";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import AddUserModal from "@Components/SpaceSettings/UserAdministration/ReviewFlows/ReviewFlowReviewLevels/AddUserModal";
import ReviewStageTransitionChart from "./ReviewStageTransitionChart";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import { Checkbox, TextField, Box } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import IconComponent from "@CubeComponents/icons";
import Text from "@CubeComponents/html/Text/Text";
import type { AutocompleteRenderInputParams } from "@mui/material/Autocomplete";
import type { ReviewApprovalStage } from "./ReviewsHelper";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

/**
 * One reviewer row in the notify list. Deliberately structural rather than derived: callers pass either
 * an approval-stage reviewer (`approvalStageReviewerSchema`, which carries `response`) or a reviewFlow
 * review-level reviewer (which does not), and this component only ever reads these four fields.
 *
 * A `type` alias (not an interface) so it satisfies `AutoComplete`'s `Record<string, unknown>` option
 * constraint via TypeScript's implicit index signature.
 */
type NotificationReviewer = {
  userId: string;
  firstName?: string;
  lastName?: string;
  profilePic?: string | null;
};

/**
 * One space user handed back by `AddUserModal`'s `addUserCallback`. That component's props are still
 * `any`, and there is no server response type for its picker, so only the fields read here are declared.
 */
interface NotificationSpaceUser {
  userId: string;
  firstName?: string;
  lastName?: string;
  profilePic?: string | null;
}

/** Exactly the three fields `confirmCallback` receives (they map to the reviews notification args). */
interface ReviewNotificationConfirmPayload {
  message: string;
  selectedReviewerIdsFromSpace: string[];
  selectedReviewerIdsAtTargetLevel: string[];
}

interface ReviewNotificationModalState {
  selectedReviewersAtTargetLevel: string[];
  selectedReviewersFromSpace: NotificationSpaceUser[];
  message: string;
}

interface ReviewNotificationModalProps {
  /**
   * KNOWN BUG (see the defaults below): these two are plain `string`, not `TranslatedString`, because
   * neither this component nor its callers route them through `t()`.
   */
  readonly modalTitle?: string;
  readonly confirmBtnTitle?: string;
  readonly targetLevelReviewers?: NotificationReviewer[];
  readonly targetLevelSerial?: number;
  readonly spaceId?: string;
  readonly cancelCallback?: () => void;
  readonly confirmCallback?: (payload: ReviewNotificationConfirmPayload) => void;
  readonly approvalStages?: ReviewApprovalStage[];
  readonly action?: string | null;
  readonly requireMandatoryComment?: boolean;
  readonly skipReviewTillLevel?: number | null;
  readonly rollbackReviewToLevel?: number | null;
  readonly isLoadingApprove?: boolean;
  readonly setIsLoadingApprove?: (loading: boolean) => void;
  /** Accepted for call-site symmetry but never destructured/used by this component. */
  readonly checkBoxToAssignUserToTransition?: unknown;
}

function ReviewNotificationModal({
  // KNOWN BUG, preserved verbatim: both defaults are raw English literals, so a modal opened without
  // these props shows untranslated copy (and `ReviewDetails`/`ReviewProperties` pass plain strings too).
  // Does the server read them? No — display only. Fix: wrap in `t()` and add the keys to en.json; a
  // `t()` call here would change the emitted default, so they are left alone.
  modalTitle = "Send Notification",
  confirmBtnTitle = "Confirm",
  targetLevelReviewers = [],
  targetLevelSerial,
  spaceId,
  cancelCallback,
  confirmCallback,
  approvalStages = [],
  action,
  requireMandatoryComment = false,
  skipReviewTillLevel,
  rollbackReviewToLevel,
  isLoadingApprove,
  setIsLoadingApprove,
}: ReviewNotificationModalProps) {
  const { t } = useTranslation();
  const initialTargetLevelReviewerIds = targetLevelReviewers.map((record) => record.userId);

  const defaultReviewNotificationData: ReviewNotificationModalState = {
    selectedReviewersAtTargetLevel: initialTargetLevelReviewerIds,
    selectedReviewersFromSpace: [],
    message: "",
  };

  const [modalData, setModalData] = useState<ReviewNotificationModalState>(defaultReviewNotificationData);
  const [spaceUsersModal, setSpaceUsersModal] = useState<{ show: boolean }>({ show: false });
  const [selectedReviewers, setSelectedReviewers] = useState<NotificationReviewer[]>(targetLevelReviewers);
  const notificationMultiSelectRef = useRef<{ value: string[] }>({ value: [] });
  const [disabledIds, setDisabledIds] = useState<string[]>([]);

  const messageOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    setModalData((prev) => ({ ...prev, message: e.target.value }));
  };

  const closeAddUserModal = () => {
    setSpaceUsersModal({ show: false });
  };

  const openUsersInSpaceList = () => {
    setSpaceUsersModal({ show: true });
  };

  const addUserCallback = (selectedUsers: NotificationSpaceUser[]) => {
    const selectedUsersMapper = selectedUsers.map(({ userId, firstName, lastName, profilePic }) => ({
      userId,
      firstName,
      lastName,
      profilePic,
    }));

    const currentOtherSpaceUsers = modalData.selectedReviewersFromSpace;
    const currentOtherSpaceUserIds = currentOtherSpaceUsers.map((user) => user.userId);
    const currentLevel1UsersIds = selectedReviewers.map((r) => r.userId);

    const totalCurrentUserIds = new Set<string>([...currentLevel1UsersIds, ...currentOtherSpaceUserIds]);

    const newUsersToAdd = selectedUsersMapper.filter((user) => !totalCurrentUserIds.has(user.userId));
    const totalUsersInOtherSpaceList = [...currentOtherSpaceUsers, ...newUsersToAdd];

    setModalData((prev) => ({
      ...prev,
      selectedReviewersFromSpace: totalUsersInOtherSpaceList,
    }));
    closeAddUserModal();
  };

  const removeReviewerFromLevelNotificationList = useCallback((userId: string) => {
    setSelectedReviewers((prev) => {
      const updated = prev.filter((u) => u.userId !== userId);
      notificationMultiSelectRef.current.value = updated.map((u) => u.userId);
      return updated;
    });

    setModalData((prevModal) => ({
      ...prevModal,
      selectedReviewersAtTargetLevel: prevModal.selectedReviewersAtTargetLevel.filter((id) => id !== userId),
    }));
  }, []);

  const removeReviewerFromSpaceNotificationList = useCallback((userId: string) => {
    setModalData((prev) => ({
      ...prev,
      selectedReviewersFromSpace: prev.selectedReviewersFromSpace.filter((user) => user.userId !== userId),
    }));
  }, []);

  const confirmNotificationHandler = () => {
    if (typeof setIsLoadingApprove === "function") {
      setIsLoadingApprove(true);
    }

    const { message, selectedReviewersFromSpace } = modalData;

    const selectedReviewerIdsAtTargetLevel = selectedReviewers.map((r) => r.userId);
    const selectedReviewerIdsFromSpace = selectedReviewersFromSpace.map((user) => user.userId);

    // KNOWN BUG, preserved verbatim: `confirmCallback` is optional in the prop list yet invoked without
    // a guard, so mounting this modal without one throws on Confirm. Does the server read it? No — it is
    // the caller's submit handler. Fix: make the prop required, or guard (which changes the emit).
    confirmCallback!({
      message,
      selectedReviewerIdsFromSpace,
      selectedReviewerIdsAtTargetLevel,
    });
  };

  useEffect(() => {
    if (targetLevelSerial && targetLevelReviewers?.length) {
      notificationMultiSelectRef.current.value = initialTargetLevelReviewerIds;
    }
  }, []);

  const getDefaultUserNameComponent = (user: NotificationSpaceUser) => {
    return (
      <UserNameTagComponent
        id={user.userId}
        handleDeleteIconClicked={() => removeReviewerFromSpaceNotificationList(user.userId)}
      />
    );
  };

  const handleSelect = useCallback((event: SyntheticEvent, newValue: readonly (NotificationReviewer | string)[]) => {
    // `typeof item === "object"` narrows the element for the `.userId` read; the result is re-typed with
    // an erased cast rather than a type predicate, because a predicate would need `!!` (emitted code).
    const updatedSelected = newValue.filter(
      (item) => typeof item === "object" && item.userId,
    ) as NotificationReviewer[];

    setSelectedReviewers(updatedSelected);

    const selectedUserIds = new Set<string>(updatedSelected.map((u) => u.userId));
    const isNotSelected = (user: NotificationSpaceUser) => !selectedUserIds.has(user.userId);

    setModalData((prev) => ({
      ...prev,
      selectedReviewersAtTargetLevel: updatedSelected.map((u) => u.userId),
      selectedReviewersFromSpace: prev.selectedReviewersFromSpace?.filter(isNotSelected),
    }));
  }, []);

  const notificationHeader = () => <p className="w-full text-2xl font-bold rounded-t-2">{modalTitle}</p>;

  const renderOptionItem = useCallback(
    (props: HTMLAttributes<HTMLLIElement>, option: NotificationReviewer, { selected }: { selected: boolean }) => (
      <li {...props} id={`itemTemplate_user_${option.userId}`}>
        <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginInlineEnd: "0.5rem" }} checked={selected} />
        <UserNameTagComponent id={option.userId} />
      </li>
    ),
    [],
  );

  const renderTagItem = useCallback(
    (option: NotificationReviewer) => (
      <Box key={option.userId} className="flex flex-row items-center pt-1" id={`valueTemplate_user_${option.userId}`}>
        <UserNameTagComponent
          id={option.userId}
          handleDeleteIconClicked={() => removeReviewerFromLevelNotificationList(option.userId)}
        />
      </Box>
    ),
    [removeReviewerFromLevelNotificationList],
  );

  const renderInputField = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField {...params} variant="outlined" placeholder={t("Reviewers")} />
    ),
    [t],
  );

  const getReviewerMultiSelect = useCallback(
    () => (
      <AutoComplete
        multiple
        disableCloseOnSelect
        options={targetLevelReviewers}
        value={selectedReviewers}
        onChange={handleSelect}
        disablePortal
        getOptionLabel={(option) => `${option.firstName ?? ""} ${option.lastName ?? ""}`.trim()}
        isOptionEqualToValue={(opt, val) => opt.userId === val.userId}
        renderOption={renderOptionItem}
        renderTags={(value) => value.map(renderTagItem)}
        renderInput={renderInputField}
      />
    ),
    [targetLevelReviewers, selectedReviewers, handleSelect, renderOptionItem, renderTagItem, renderInputField],
  );

  useEffect(() => {
    setDisabledIds([
      ...modalData.selectedReviewersFromSpace.map((user) => user.userId),
      ...modalData.selectedReviewersAtTargetLevel,
    ]);
  }, [modalData]);

  const notificationContent = () => {
    const isRollback = action === "rollbackReview";

    return (
      <div className="p-0">
        <div className="pt-8 pb-8 ps-8 pe-4 border-b-2 border-system_colour_44 mx-auto">
          {approvalStages.length ? (
            <ReviewStageTransitionChart
              approvalStages={approvalStages}
              isRollback={isRollback}
              skipReviewTillLevel={skipReviewTillLevel}
              rollbackReviewToLevel={rollbackReviewToLevel}
            />
          ) : undefined}
        </div>
        {targetLevelSerial && (
          <div
            className="py-6 px-8 pb-4 flex flex-col gap-4 justify-start border-b-0.5 border-[#ecf3fe] last:border-b-0 [&_p]:text-base [&_p]:font-[var(--font-weight-600)] [&_span]:text-xs [&_span]:font-[var(--font-weight-400)]"
            id="reviewsAutoNotificationWrapper"
          >
            <p>
              <Text>{t("Notify Reviewers at Level {{level}}", { level: targetLevelSerial })}</Text>
            </p>
            {getReviewerMultiSelect()}
            <span>
              <Text>
                {t(
                  "Email Notifications are only sent to reviewers listed above. Any reviewer for this task can take action during the review task.",
                )}
              </Text>
            </span>
          </div>
        )}

        <div className="py-6 px-8 pb-4 flex flex-col gap-4 justify-start border-b-0.5 border-[#ecf3fe] last:border-b-0 [&_p]:text-base [&_p]:font-[var(--font-weight-600)] [&_span]:text-xs [&_span]:font-[var(--font-weight-400)]">
          <p>
            <Text>{t("Notify Other Members of the Space")}</Text>
          </p>
          <div>
            <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={openUsersInSpaceList}>
              <div className="flex gap-1 items-center">
                <IconComponent name="add" width="1rem" height="1rem" />
                <Text>{t("Add")}</Text>
              </div>
            </ButtonComponent>
          </div>
          <div className="flex flex-row gap-2 flex-wrap items-center justify-start">
            {modalData.selectedReviewersFromSpace.map((user) => getDefaultUserNameComponent(user))}
          </div>
          <span>
            <Text>{t("Notified space members can read review comments.")}</Text>
          </span>
        </div>

        <div className="py-6 px-8 pb-4 flex flex-col gap-4 justify-start border-b-0.5 border-[#ecf3fe] last:border-b-0 [&_p]:text-base [&_p]:font-[var(--font-weight-600)] [&_span]:text-xs [&_span]:font-[var(--font-weight-400)]">
          {requireMandatoryComment ? (
            <p>
              <Text>{t("Custom Message*")}</Text>
            </p>
          ) : (
            <p>
              <Text>{t("Custom Message")}</Text>
            </p>
          )}
          <TextFieldComponent
            multiline
            value={modalData.message}
            placeholder={t("Enter notification message")}
            rows={5}
            height="auto"
            onChange={messageOnChange}
          />
        </div>
      </div>
    );
  };

  const notificationFooter = () => (
    <div className="flex flex-row gap-4 items-center justify-start">
      <ButtonComponent
        onClick={confirmNotificationHandler}
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        disabled={requireMandatoryComment && !modalData.message.trim()}
        loading={isLoadingApprove}
      >
        {confirmBtnTitle}
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={cancelCallback}>
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </div>
  );

  return (
    <>
      <ModalStateDialogComponent
        isVisible
        allowModalPadding={false}
        width="62.5rem"
        headerTitle={notificationHeader()}
        onCancel={cancelCallback}
        modelContent={notificationContent()}
        footerContent={notificationFooter()}
      />

      {spaceUsersModal.show && (
        <AddUserModal
          spaceId={spaceId}
          addUserCallback={addUserCallback}
          closeModal={closeAddUserModal}
          disabledUserIds={disabledIds}
          checkBoxToAssignUserToTransition={undefined}
        />
      )}
    </>
  );
}

export default ReviewNotificationModal;
